import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Eye, Edit, Trash } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Reactable } from "../../components/ui/reactable";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CYCLES } from "../../constants/api";

import { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const cycleStatusStyles = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200",
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    closed: "bg-slate-200 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200",
};

const STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
    { value: "closed", label: "Closed" },
];

const range = (from, to) =>
    `${dayjs(from).format("DD-MM-YYYY")} – ${dayjs(to).format("DD-MM-YYYY")}`;

/* Every filter is a column, so they all live in Reactable's funnel and the
   screen keeps no loose controls of its own. */
const buildColumns = ({ canEditCycle, onView, onEdit, onDelete }) => [
    {
        accessor: "name", Header: "Name", sortable: true, filterable: true,
        Cell: ({ row }) => (
            <div>
                <button
                    className="font-medium hover:text-sidebar-primary text-left"
                    onClick={(e) => { e.stopPropagation(); onView(row.id); }}
                >
                    {row.name}
                </button>
                <p className="text-xs text-muted-foreground line-clamp-1 sm:hidden">
                    {range(row.period_start, row.period_end)}
                </p>
            </div>
        ),
    },
    {
        accessor: "period_start", Header: "Period", sortable: true,
        filterable: true, filterType: "date",
        Cell: ({ row }) => range(row.period_start, row.period_end),
    },
    {
        accessor: "self_eval_start", Header: "Self-Eval Window", sortable: true,
        filterable: true, filterType: "date",
        Cell: ({ row }) => range(row.self_eval_start, row.self_eval_end),
    },
    {
        accessor: "status", Header: "Status", sortable: true, filterable: true,
        filterType: "select", filterOptions: STATUS_OPTIONS,
        Cell: ({ value }) => (
            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full capitalize ${cycleStatusStyles[value] || cycleStatusStyles.draft}`}>
                {value}
            </span>
        ),
    },
    {
        accessor: "actions", Header: "Actions",
        Cell: ({ row }) => (
            <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" title="View"
                    onClick={(e) => { e.stopPropagation(); onView(row.id); }}>
                    <Eye className="w-4 h-4" />
                </Button>
                {canEditCycle && (
                    <>
                        <Button variant="ghost" size="icon" title="Edit"
                            onClick={(e) => { e.stopPropagation(); onEdit(row.id); }}>
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Delete"
                            className="text-red-500 hover:text-red-600"
                            onClick={(e) => { e.stopPropagation(); onDelete(row); }}>
                            <Trash className="w-4 h-4" />
                        </Button>
                    </>
                )}
            </div>
        ),
    },
];

const AppraisalCycleList = () => {
    const navigate = useNavigate();
    const { fetch, remove } = useApiMethods();
    const { canAddCycle, canEditCycle } = useAppraisalPermissions();

    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_CYCLES);
            setCycles(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load cycles");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await remove(`${APPRAISAL_CYCLES}?id=${deleteTarget.id}`);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Cycle removed");
                setDeleteTarget(null);
                loadData();
            } else {
                ToastMsg("error", res?.message || "Failed to remove cycle");
            }
        } catch (_) {
            ToastMsg("error", "Failed to remove cycle");
        }
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={CalendarClock}
                title="Appraisal Cycles"
                subtitle="Define the periods within which performance reviews are run"
                totalRecords={cycles.length}
                buttonEnable={canAddCycle}
                buttonName="New Cycle"
                setOpenDialog={() => navigate("/appraisal/cycles/new")}
            />

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : (
                <Reactable
                    pagename="appraisal-cycles"
                    data={cycles}
                    setData={setCycles}
                    columns={buildColumns({
                        canEditCycle,
                        onView: (id) => navigate(`/appraisal/cycles/${id}`),
                        onEdit: (id) => navigate(`/appraisal/cycles/edit/${id}`),
                        onDelete: (c) => setDeleteTarget(c),
                    })}
                    rowSize="sm"
                    tableHeight="calc(100vh - 300px)"
                />
            )}

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Delete Cycle"
                description={`Are you sure you want to delete "${deleteTarget?.name}"? Existing appraisals in this cycle will lose their reference.`}
                destructive
                confirmLabel="Delete"
                type="danger"
            />
        </ContainerCard>
    );
};

export default AppraisalCycleList;
